/**
 * scheduleEmployeeSchedule.ts
 * Frontend service for the Employee Schedule weekly capacity API.
 *
 * Phase 3, Item 2 — Employee Schedule page.
 * Phase 3, Item 3 — Employee Week Detail (expand-row Gantt strip).
 * Phase 3, Item 5 — Add Assignment dialog (createEmployeeAssignment, fetchWorkingDays).
 *
 * All requests go through apiClient (MSAL auth + base URL + error capture).
 *
 * Endpoints:
 *   GET  /api/schedule/employee-weeks?startWeek=YYYY-MM-DD&weekCount=N
 *   GET  /api/schedule/employee-week-detail?userKey=UUID&weekStart=YYYY-MM-DD
 *   GET  /api/schedule/working-days?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD&region=BC
 *   POST /api/schedule/employee-assignments
 */

import apiClient from './apiClient';

// ---------------------------------------------------------------------------
// Response types (mirror employeeWeeksController.ts)
// ---------------------------------------------------------------------------

export interface EmployeeWeekUser {
  userKey: string;
  displayName: string;
  jobClass: string | null;
  tier: string | null;
  workGroup: string | null;
  photoUrl: string | null;
}

/**
 * A DIFFERENT draft scenario that holds soft (tentative) load for a person.
 * Mirrors CrossScenarioSource (crossScenarioContentionService.ts). Powers the
 * "also held in another plan" attribution (Code · Name — owner).
 */
export interface CrossScenarioSource {
  scenarioKey: string;
  code: string | null;
  name: string | null;
  owner: string | null;
}

/** Format a soft-conflict source as "SP-003 · Q3 Plan B — owned by jdoe@bgc". */
export function formatCrossScenarioSource(s: CrossScenarioSource): string {
  const codeName = [s.code, s.name].filter(Boolean).join(' · ') || s.scenarioKey;
  return s.owner ? `${codeName} — owned by ${s.owner}` : codeName;
}

export interface EmployeeWeekCell {
  userKey: string;
  /** ISO date 'YYYY-MM-DD' — always a Saturday */
  weekStart: string;
  /**
   * LEGACY-scale weekly sum under the Wave-3 binary model: busy day = 100,
   * task-on-absence conflict day = 200. Prefer the additive busyDays /
   * workingDays fields for the Q5B percent-of-week figure.
   */
  totalPercent: number;
  /**
   * Wave 3 (Q3B): true ONLY when a task lands on a day the person has a dated
   * absence — the one remaining hard conflict. Task stacking never flags.
   */
  hasOverbook: boolean;
  /**
   * Per-DAY load breakdown (LEGACY scale: busy = 100, conflict = 200): ISO date
   * → load. Only working days that carry load appear. Optional for back-compat.
   */
  byDay?: Record<string, number>;
  /**
   * Wave 3: days NEWLY MADE BUSY by this plan (100 on days busy only through
   * this plan's rows). `existing = byDay − byDayTentative` still holds. Optional.
   */
  byDayTentative?: Record<string, number>;
  /** Wave-3 additive: working day → task count (0-count days included — Q1B chip). */
  byDayTaskCount?: Record<string, number>;
  /** Wave-3 additive: working day → covered by a dated absence (any type — Q2A). */
  byDayAbsent?: Record<string, boolean>;
  /** Wave-3 additive: count of busy working days (Q5B numerator). */
  busyDays?: number;
  /** Wave-3 additive: working days in the week (holiday-aware denominator). */
  workingDays?: number;
  /**
   * Cross-scenario SOFT holds (ISO date → COUNT of other draft plans holding the
   * day; P2-6 presence rule — >0 = held, never a percentage) — tentative holds
   * OTHER draft scenarios placed on this person/day. NEVER part of
   * byDay/totalPercent/hasOverbook. Drives the "also held in another plan" soft
   * overlay (2026-06-22; presence since audit #2). Optional.
   */
  byDaySoft?: Record<string, number>;
  /**
   * Staffing Allocation % (PR A/B, 2026-07-13) — Σ intentional allocation %
   * over the DISTINCT WO/quote sources this person is present in that week
   * (counted ONCE per source; presence-fallback 100). The utilization number
   * the capacity grid renders as under/healthy/at/over color bands (D5). >100
   * warns, never blocks (D4). 0 for absence-only / soft-only weeks. Optional
   * for back-compat with a stale backend.
   */
  allocationPercent?: number;
  /**
   * Staff-this-job redesign (2026-07-14) — the committed / uncommitted-other /
   * this-project split of `allocationPercent` (the three sum to it). Present
   * only when the allocation overlay succeeds. `thisSourcePercent` is the
   * viewed scenario's OWN source share (the editable "this project" slice);
   * `committedPercent` is firm/live-schedule load; `tentativeOtherPercent` is
   * load from OTHER draft scenarios. Powers the split availability bar
   * (committed = firm green, uncommitted = other-draft amber hatch,
   * this-project = editable blue, free = remainder).
   */
  committedPercent?: number;
  tentativeOtherPercent?: number;
  thisSourcePercent?: number;
  /**
   * BUG-2026-0175 — Σ tentative load from OTHER DRAFT plans' sources (the
   * bar's "Uncommitted — tentative, other draft plans"). Non-zero only on a
   * draft overlay; ADDITIVE — never included in `allocationPercent` (so bands
   * and advisories reading the total are unchanged). Consumers add it to
   * `tentativeOtherPercent` for the amber Uncommitted segment.
   */
  otherDraftTentativePercent?: number;
}

export interface EmployeeWeeksResponse {
  /** Ordered list of ISO date strings (Saturdays), length = weekCount */
  weekStarts: string[];
  /** Active assignable users with their scheduling profile */
  users: EmployeeWeekUser[];
  /** Capacity aggregates — one row per (userKey × weekStart) that has data */
  cells: EmployeeWeekCell[];
  /**
   * Per-user (LOWERCASED key) attribution for the cross-scenario soft load — which
   * OTHER draft scenarios hold this person. Present only for users with soft load in
   * the window. Powers the soft markers' tooltips + the assign toast (2026-06-22).
   */
  crossScenarioSources?: Record<string, CrossScenarioSource[]>;
}

// ---------------------------------------------------------------------------
// Service function
// ---------------------------------------------------------------------------

/**
 * Fetch the weekly employee capacity grid data.
 *
 * @param startWeek   ISO date 'YYYY-MM-DD' — must be a Saturday.
 *                    If omitted, the backend defaults to the current Saturday.
 * @param weekCount   How many weeks to include (1–52). Defaults to 8.
 * @param scenarioKey Optional scenario key (FEAT-2026-0007). When provided,
 *                    the backend overlays that draft's Tentative rows on the
 *                    committed baseline.
 */
export async function fetchEmployeeWeeks(options?: {
  startWeek?: string;
  weekCount?: number;
  scenarioKey?: string;
}): Promise<EmployeeWeeksResponse> {
  const params = new URLSearchParams();
  if (options?.startWeek) params.set('startWeek', options.startWeek);
  if (options?.weekCount !== undefined) params.set('weekCount', String(options.weekCount));
  if (options?.scenarioKey) params.set('scenarioKey', options.scenarioKey);

  const qs = params.toString();
  const url = qs ? `/schedule/employee-weeks?${qs}` : '/schedule/employee-weeks';

  const response = await apiClient.get<EmployeeWeeksResponse>(url);
  return response.data;
}

// ---------------------------------------------------------------------------
// Phase 3, Item 3 — Employee Week Detail (expand-row Gantt strip)
// ---------------------------------------------------------------------------

/**
 * One assignment bar for the per-week Gantt strip.
 * Mirrors AssignmentBarDetail from employeeWeekDetailController.ts.
 */
export interface AssignmentBarDetail {
  assignmentKey: string;
  /** Task name or absence type label. */
  name: string;
  /** ISO date 'YYYY-MM-DD' — effective start within the week. */
  startDate: string;
  /** ISO date 'YYYY-MM-DD' — effective end within the week. */
  endDate: string;
  /** Legacy wire field — binary scale under Wave 3 (a covered row reads 100).
   *  Screens key on presence/state, not this number. */
  percent: number;
  /** Determines bar color. */
  sourceType: 'Task' | 'Vacation' | 'Sick' | 'FieldWork' | 'Other';
  scheduleTaskKey: string | null;
  quoteKey: string | null;
  /** Human-readable quote number (e.g. "Q-2026-001") for the source chip. */
  quoteNumber: string | null;
  workOrderKey: string | null;
  /** Human-readable work-order id (e.g. "WO-2026-014") for the source chip. */
  workOrderNo: string | null;
  /** Descriptive WorkOrder name (e.g. "Kemano T2 Tunnel Rock Testing") — popup context. */
  workOrderName: string | null;
  /** Descriptive Project name — popup context line. */
  projectName: string | null;
  /**
   * ProjectKey (UUID) from the Project linked through the WorkOrder.
   * Null for Quote-sourced tasks, absences, and manual tasks.
   * Follow-up 1.8.
   */
  projectKey: string | null;
  /**
   * ProjectId (human-readable, e.g. "BGC-2026-001") for building the full deep-link:
   * /projects/{projectId}/workorders/{workOrderKey}.
   * Null for Quote-sourced tasks, absences, and manual tasks.
   * Follow-up 1.8.
   */
  projectId: string | null;
  status: 'Tentative' | 'Committed';
}

/**
 * Staffing Allocation % (PR C / D6) — one entry per DISTINCT work order / quote
 * (or "Manual") the person is on that week, with its allocation %. Mirrors
 * SourceAllocationDetail from employeeWeekDetailController.ts. The entries SUM
 * to the week's utilization %; the popup renders them as the per-source breakdown.
 */
export interface SourceAllocationDetail {
  sourceType: 'WorkOrder' | 'Quote' | 'Manual';
  sourceKey: string | null;
  label: string;
  name: string | null;
  percent: number;
}

export interface EmployeeWeekDetailResponse {
  userKey: string;
  weekStart: string;
  weekEnd: string;
  assignments: AssignmentBarDetail[];
  /** Per-source allocation breakdown (D6) — sums to the week utilization %. */
  sourceAllocations?: SourceAllocationDetail[];
}

/**
 * Fetch the per-assignment detail for a single user × week (expand-row Gantt strip).
 * Only called when the user expands a row — lazy-loaded.
 *
 * @param userKey     UUID of the AppUser.
 * @param weekStart   ISO date 'YYYY-MM-DD' — must be a Saturday.
 * @param scenarioKey Optional scenario key (FEAT-2026-0007). When provided,
 *                    the backend overlays that draft's Tentative assignments on
 *                    the committed baseline so the popover is scenario-scoped.
 */
export async function fetchEmployeeWeekDetail(
  userKey: string,
  weekStart: string,
  scenarioKey?: string,
): Promise<EmployeeWeekDetailResponse> {
  const params = new URLSearchParams({ userKey, weekStart });
  if (scenarioKey) params.set('scenarioKey', scenarioKey);
  const response = await apiClient.get<EmployeeWeekDetailResponse>(
    `/schedule/employee-week-detail?${params.toString()}`,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Phase 3, Item 5 — Add Assignment dialog
// ---------------------------------------------------------------------------

/** One holiday entry returned from GET /api/schedule/working-days */
export interface HolidayEntry {
  date: string;
  name: string;
}

/** Response from GET /api/schedule/working-days */
export interface WorkingDaysResponse {
  workingDayCount: number;
  /** All stat holidays in [startDate, endDate] — use for date-picker greying */
  holidaysInRange: HolidayEntry[];
}

/**
 * Fetch working-day count and stat holidays for a date range.
 * Used by the Add Assignment dialog to compute percent and grey-out holidays.
 *
 * @param startDate  ISO date 'YYYY-MM-DD'.
 * @param endDate    ISO date 'YYYY-MM-DD'.
 * @param region     Optional region code (default 'NB' — the lab's province).
 */
export async function fetchWorkingDays(
  startDate: string,
  endDate: string,
  region = 'NB',
): Promise<WorkingDaysResponse> {
  const params = new URLSearchParams({ startDate, endDate, region });
  const response = await apiClient.get<WorkingDaysResponse>(
    `/schedule/working-days?${params.toString()}`,
  );
  return response.data;
}

/**
 * Fetch only the stat holidays in a date range — used by the schedule views to
 * shade non-work days (weekends are computed client-side; holidays are not).
 *
 * Deliberately does NOT pass a `region`: the server now defaults the region to
 * NB. (fetchWorkingDays still accepts an explicit region for the Add Assignment
 * dialog's percent math; this thin wrapper reuses the same endpoint + types but
 * returns just the holiday list the shading needs.)
 *
 * @param startDate  ISO date 'YYYY-MM-DD'.
 * @param endDate    ISO date 'YYYY-MM-DD'.
 */
export async function fetchHolidaysInRange(
  startDate: string,
  endDate: string,
): Promise<HolidayEntry[]> {
  const params = new URLSearchParams({ startDate, endDate });
  const response = await apiClient.get<WorkingDaysResponse>(
    `/schedule/working-days?${params.toString()}`,
  );
  return response.data.holidaysInRange;
}

// Note: createEmployeeAssignment lives in scheduleAssignments.ts to avoid duplication.
// The dialog imports fetchWorkingDays from here and createEmployeeAssignment from there.
